import { useState, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useDispatch } from 'react-redux';
import { alertSlice } from 'src/store/alert/alert.slice';
import { fetchApi, fetchWithError } from 'src/react-query/fetchWithError';
import { queryKeys } from 'src/react-query/queryKeys';
import { 
  partnerSendCommandUrl, 
  partnerCommandStatusUrl, 
  partnerCommandsUrl 
} from 'src/react-query/endPoints';

export const useSendCommand = () => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (commandData) => fetchApi({
      url: partnerSendCommandUrl,
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: commandData,
    }),
    onSuccess: () => {
      dispatch(
        alertSlice.actions.setAlert({
          open: true,
          message: 'Command sent successfully',
          severity: 'success'
        })
      );
      // Invalidate commands list to refresh
      queryClient.invalidateQueries(['partnerCommands']);
    },
    onError: (error) => {
      dispatch(
        alertSlice.actions.setAlert({
          open: true,
          message: error?.response?.data?.status_message || error?.message || 'Failed to send command',
          severity: 'error'
        })
      );
    },
  });
};

export const useCommandStatus = (commandId) => {
  return useQuery({
    queryKey: queryKeys.partnerCommandStatus(commandId),
    queryFn: () => fetchWithError(partnerCommandStatusUrl(commandId)),
    enabled: !!commandId,
    staleTime: 30 * 1000, // 30 seconds
    gcTime: 5 * 60 * 1000, // 5 minutes
    refetchInterval: (data) => {
      // Stop polling if command is completed or failed
      if (data?.status === 'COMPLETED' || data?.status === 'FAILED' || data?.status === 'CANCELLED') {
        return false;
      }
      return 5000; // Poll every 5 seconds for active commands
    },
  });
};

export const usePartnerCommands = (partnerId, params = {}) => {
  return useQuery({
    queryKey: queryKeys.partnerCommands(partnerId, params),
    queryFn: () => fetchWithError(partnerCommandsUrl(partnerId, params)),
    enabled: !!partnerId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });
};

export const useAllCommands = (params = {}) => {
  return useQuery({
    queryKey: ['allCommands', params],
    queryFn: () => fetchWithError(partnerCommandsUrl('all', params)),
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });
}; 

export const useCommandData = () => {
  const [commands, setCommands] = useState([]);
  const [metrics, setMetrics] = useState({
    queueDepth: 0,
    processing: 0,
    successRate: 75.5,
    throughputPerMin: 12,
    avgExecutionTime: 2.3,
    totalItems: 156,
    completed: 118,
    failed: 38,
    avgResponseTime: 1.8,
    totalCommands: 156,
    activePartners: 3
  });
  const [queueData, setQueueData] = useState({
    status: 'PROCESSING',
    items: [],
    performance: {
      successRate: 75.5,
      avgExecutionTime: 2.3,
      totalItems: 156,
      completed: 118,
      failed: 38
    }
  });
  const [isLoading, setIsLoading] = useState(false);

  const dispatch = useDispatch();

  // Computed values
  const activeCommands = commands.filter(cmd => 
    cmd.status === 'PENDING' || cmd.status === 'PROCESSING'
  );

  const commandStats = {
    total: commands.length,
    pending: commands.filter(cmd => cmd.status === 'PENDING').length,
    processing: commands.filter(cmd => cmd.status === 'PROCESSING').length,
    completed: commands.filter(cmd => cmd.status === 'COMPLETED').length,
    failed: commands.filter(cmd => cmd.status === 'FAILED').length,
    cancelled: commands.filter(cmd => cmd.status === 'CANCELLED').length
  };

  const commandDistribution = [
    { name: 'START_SESSION', value: commands.filter(cmd => cmd.type === 'START_SESSION').length },
    { name: 'STOP_SESSION', value: commands.filter(cmd => cmd.type === 'STOP_SESSION').length },
    { name: 'UNLOCK_CONNECTOR', value: commands.filter(cmd => cmd.type === 'UNLOCK_CONNECTOR').length },
    { name: 'RESERVE_NOW', value: commands.filter(cmd => cmd.type === 'RESERVE_NOW').length },
    { name: 'CANCEL_RESERVATION', value: commands.filter(cmd => cmd.type === 'CANCEL_RESERVATION').length }
  ];

  const commandHistory = commands.filter(cmd => 
    cmd.status === 'COMPLETED' || cmd.status === 'FAILED' || cmd.status === 'CANCELLED'
  );

  // Analytics data
  const analytics = {
    trends: [],
    distribution: commandDistribution.map(item => ({
      ...item,
      color: getCommandColor(item.name)
    })),
    statusDistribution: [
      { name: 'COMPLETED', value: commandStats.completed, color: '#00C851' },
      { name: 'FAILED', value: commandStats.failed, color: '#ff4444' },
      { name: 'PENDING', value: commandStats.pending, color: '#ffbb33' },
      { name: 'PROCESSING', value: commandStats.processing, color: '#33b5e5' }
    ],
    partnerPerformance: []
  };

  // Helper function for command colors
  function getCommandColor(commandType) {
    switch (commandType) {
      case 'START_SESSION': return '#00C851';
      case 'STOP_SESSION': return '#ff4444';
      case 'UNLOCK_CONNECTOR': return '#33b5e5';
      case 'RESERVE_NOW': return '#ffbb33';
      case 'CANCEL_RESERVATION': return '#9933cc';
      default: return '#888888';
    }
  }

  // Command management functions
  const addCommand = useCallback((command) => {
    try {
      const newCommand = {
        id: `cmd_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        ...command,
        status: 'PENDING',
        created: new Date().toISOString(),
        updated: new Date().toISOString(),
        partner: command.partner || 'Unknown Partner'
      };
      
      setCommands(prev => [...prev, newCommand]);
      
      dispatch(
        alertSlice.actions.setAlert({
          open: true,
          message: `Command ${command.type} added successfully`,
          severity: 'success'
        })
      );
      
      return newCommand;
    } catch (error) {
      dispatch(
        alertSlice.actions.setAlert({
          open: true,
          message: error?.message || 'Failed to add command',
          severity: 'error'
        })
      );
    }
  }, [dispatch]);

  const updateCommand = useCallback((commandId, updates) => {
    try {
      setCommands(prev => 
        prev.map(cmd => 
          cmd.id === commandId 
            ? { ...cmd, ...updates, updated: new Date().toISOString() }
            : cmd
        )
      );
      
      dispatch(
        alertSlice.actions.setAlert({
          open: true,
          message: 'Command updated successfully',
          severity: 'success'
        })
      );
    } catch (error) {
      dispatch(
        alertSlice.actions.setAlert({
          open: true,
          message: error?.message || 'Failed to update command',
          severity: 'error'
        })
      );
    }
  }, [dispatch]);

  const deleteCommand = useCallback((commandId) => {
    try {
      setCommands(prev => prev.filter(cmd => cmd.id !== commandId));
      
      dispatch(
        alertSlice.actions.setAlert({
          open: true,
          message: 'Command deleted successfully',
          severity: 'success'
        })
      );
    } catch (error) {
      dispatch(
        alertSlice.actions.setAlert({
          open: true,
          message: error?.message || 'Failed to delete command',
          severity: 'error'
        })
      );
    }
  }, [dispatch]);

  const retryCommand = useCallback((commandId) => {
    try {
      updateCommand(commandId, { 
        status: 'PENDING',
        retryCount: (commands.find(cmd => cmd.id === commandId)?.retryCount || 0) + 1
      });
      
      dispatch(
        alertSlice.actions.setAlert({
          open: true,
          message: 'Command retry initiated',
          severity: 'info'
        })
      );
    } catch (error) {
      dispatch(
        alertSlice.actions.setAlert({
          open: true,
          message: error?.message || 'Failed to retry command',
          severity: 'error'
        })
      );
    }
  }, [dispatch, updateCommand, commands]);

  const cancelCommand = useCallback((commandId) => {
    try {
      updateCommand(commandId, { status: 'CANCELLED' });
      
      dispatch(
        alertSlice.actions.setAlert({
          open: true,
          message: 'Command cancelled successfully',
          severity: 'warning'
        })
      );
    } catch (error) {
      dispatch(
        alertSlice.actions.setAlert({
          open: true,
          message: error?.message || 'Failed to cancel command',
          severity: 'error'
        })
      );
    }
  }, [dispatch, updateCommand]);

  const sendQuickCommand = useCallback((command) => {
    try {
      const newCommand = addCommand(command);
      
      // Simulate processing
      setTimeout(() => {
        updateCommand(newCommand.id, { status: 'PROCESSING' });
        
        setTimeout(() => {
          updateCommand(newCommand.id, { 
            status: Math.random() > 0.2 ? 'COMPLETED' : 'FAILED',
            result: Math.random() > 0.2 ? 'Command executed successfully' : 'Command execution failed'
          });
        }, 2000);
      }, 1000);
      
      return newCommand;
    } catch (error) {
      dispatch(
        alertSlice.actions.setAlert({
          open: true,
          message: error?.message || 'Failed to send quick command',
          severity: 'error'
        })
      );
    }
  }, [addCommand, updateCommand, dispatch]);

  const sendBulkCommands = useCallback((action, commandIds) => {
    try {
      commandIds.forEach(commandId => {
        switch (action) {
          case 'CANCEL':
            cancelCommand(commandId);
            break;
          case 'RETRY':
            retryCommand(commandId);
            break;
          default:
            break;
        }
      });
      
      dispatch(
        alertSlice.actions.setAlert({
          open: true,
          message: `Bulk ${action.toLowerCase()} operation completed for ${commandIds.length} commands`,
          severity: 'success'
        })
      );
    } catch (error) {
      dispatch(
        alertSlice.actions.setAlert({
          open: true,
          message: error?.message || 'Failed to execute bulk operation',
          severity: 'error'
        })
      );
    }
  }, [cancelCommand, retryCommand, dispatch]);

  const cancelBulkCommands = useCallback((commandIds) => {
    sendBulkCommands('CANCEL', commandIds);
  }, [sendBulkCommands]);

  const refetchCommands = useCallback(() => {
    // Placeholder for refetch functionality
    console.log('Refetching commands...');
  }, []);

  return {
    // Data
    commands,
    activeCommands,
    commandHistory,
    metrics,
    queueData,
    commandStats,
    commandDistribution,
    analytics,
    
    // Loading states
    isLoading,
    
    // Actions
    addCommand,
    updateCommand,
    deleteCommand,
    retryCommand,
    cancelCommand,
    sendQuickCommand,
    sendBulkCommands,
    cancelBulkCommands,
    
    // Utilities
    refetchCommands
  };
};